import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Switch,
  Link,
  Alert,
  Skeleton,
  Snackbar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { DateField } from '../components/dropdown';
import { fetchDomains, updateDomain, type DomainListItem } from '../services/domainsApi';
import { CustomerOwnerSelect } from '../components/domains/CustomerOwnerSelect';

const REPAIRSHOPR_BASE = 'https://techsolvers.repairshopr.com';
const INVALID_STATUS = 'NO_LONGER_VALID';
// Mirrors DomainDetailPage — the values the backend's lapse guard understands.
const ACTION_OPTIONS = ['renew', 'lapse'];

/** Normalises cr001_action_on_expiry to a known option, or '' when unset. */
function actionValue(raw: string | null): string {
  const v = (raw ?? '').trim().toLowerCase();
  return ACTION_OPTIONS.includes(v) ? v : '';
}

const headerCellSx = {
  color: 'rgba(0,0,0,0.8)',
  fontSize: '0.72rem',
  fontWeight: 'bold' as const,
  textTransform: 'uppercase' as const,
};
const bodyCellSx = { fontSize: '0.8rem' };

function statusColor(status: string | null): 'success' | 'default' | 'warning' | 'error' {
  switch ((status || '').toUpperCase()) {
    case 'OK':
      return 'success';
    case INVALID_STATUS:
      return 'error';
    case '':
      return 'default';
    default:
      return 'warning';
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

type SortField = 'name' | 'businessName' | 'status' | 'expiry' | 'lastPaidDate' | 'actionOnExpiry';

function sortValue(d: DomainListItem, field: SortField): string | number {
  switch (field) {
    case 'name':
      return d.name.toLowerCase();
    case 'businessName':
      return (d.businessName ?? '').toLowerCase();
    case 'status':
      return (d.status ?? '').toLowerCase();
    case 'expiry':
      return d.expiry ? new Date(d.expiry).getTime() || 0 : 0;
    case 'lastPaidDate':
      return d.lastPaidDate ? new Date(d.lastPaidDate).getTime() || 0 : 0;
    case 'actionOnExpiry':
      // Unset sorts with 'renew' — both mean the domain will be renewed.
      return actionValue(d.actionOnExpiry) || 'renew';
  }
}

export const DomainsPage: React.FC = () => {
  const navigate = useNavigate();

  const [domains, setDomains] = useState<DomainListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [autoRenewOn, setAutoRenewOn] = useState(true);
  const [autoRenewOff, setAutoRenewOff] = useState(true);
  const [withoutOwnerOnly, setWithoutOwnerOnly] = useState(false);
  const [showInvalid, setShowInvalid] = useState(false); // NO_LONGER_VALID hidden by default
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  // Sort + pagination
  const [sort, setSort] = useState<SortField>('expiry');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [savingId, setSavingId] = useState<string | null>(null);
  const [editingOwnerId, setEditingOwnerId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to first page whenever filters/sort change.
  useEffect(() => {
    setPage(0);
  }, [search, autoRenewOn, autoRenewOff, withoutOwnerOnly, showInvalid, expiryFrom, expiryTo, sort, order]);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setDomains(await fetchDomains(signal));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const filtered = useMemo(() => {
    const fromTs = expiryFrom ? new Date(expiryFrom).getTime() : null;
    const toTs = expiryTo ? new Date(expiryTo).getTime() : null;
    return domains.filter((d) => {
      if (!showInvalid && (d.status || '').toUpperCase() === INVALID_STATUS) return false;
      if (withoutOwnerOnly && d.hasOwner) return false;
      // auto-renew on/off checkboxes; both unchecked => nothing
      if (!((d.autoRenew && autoRenewOn) || (!d.autoRenew && autoRenewOff))) return false;
      if (fromTs !== null || toTs !== null) {
        const ts = d.expiry ? new Date(d.expiry).getTime() : null;
        if (ts === null || isNaN(ts)) return false;
        if (fromTs !== null && ts < fromTs) return false;
        if (toTs !== null && ts > toTs) return false;
      }
      if (search) {
        const hay = `${d.name} ${d.businessName ?? ''} ${d.registrantEmail ?? ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
  }, [domains, showInvalid, withoutOwnerOnly, autoRenewOn, autoRenewOff, expiryFrom, expiryTo, search]);

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sort);
      const bv = sortValue(b, sort);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sort, order]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const handleSort = (field: SortField) => {
    if (sort === field) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setOrder('asc');
    }
  };

  const handleToggleAutoRenew = useCallback(async (domain: DomainListItem) => {
    const next = !domain.autoRenew;
    setSavingId(domain.id);
    setDomains((prev) => prev.map((d) => (d.id === domain.id ? { ...d, autoRenew: next } : d)));
    try {
      await updateDomain(domain.id, { autoRenew: next });
      setSnackbar({ open: true, message: `Auto-renew ${next ? 'enabled' : 'disabled'} for ${domain.name}`, severity: 'success' });
    } catch (err) {
      setDomains((prev) => prev.map((d) => (d.id === domain.id ? { ...d, autoRenew: domain.autoRenew } : d)));
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update auto-renew', severity: 'error' });
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleActionOnExpiryChange = useCallback(async (domain: DomainListItem, next: string) => {
    const previous = domain.actionOnExpiry;
    setSavingId(domain.id);
    setDomains((prev) => prev.map((d) => (d.id === domain.id ? { ...d, actionOnExpiry: next } : d)));
    try {
      await updateDomain(domain.id, { actionOnExpiry: next });
      setSnackbar({
        open: true,
        message: next === 'lapse'
          ? `${domain.name} will be allowed to lapse — it won't be invoiced or scheduled`
          : `${domain.name} will be renewed`,
        severity: 'success',
      });
    } catch (err) {
      setDomains((prev) => prev.map((d) => (d.id === domain.id ? { ...d, actionOnExpiry: previous } : d)));
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update action on expiry',
        severity: 'error',
      });
    } finally {
      setSavingId(null);
    }
  }, []);

  const sortableHeader = (field: SortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell sx={{ ...headerCellSx, textAlign: align }}>
      <TableSortLabel active={sort === field} direction={sort === field ? order : 'asc'} onClick={() => handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" gutterBottom>Domains</Typography>

      {/* Filter bar — checkboxes + expiry date range + search (mirrors InvoicesPage) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search domain, business, or registrant email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 150 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRenewOn} onChange={(e) => setAutoRenewOn(e.target.checked)} />}
            label={<Typography variant="body2">Auto-renew on</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRenewOff} onChange={(e) => setAutoRenewOff(e.target.checked)} />}
            label={<Typography variant="body2">Auto-renew off</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={withoutOwnerOnly} onChange={(e) => setWithoutOwnerOnly(e.target.checked)} />}
            label={<Typography variant="body2">Without owner</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={showInvalid} onChange={(e) => setShowInvalid(e.target.checked)} />}
            label={<Typography variant="body2">Show invalid</Typography>}
          />
          <div style={{ width: 144 }}>
            <DateField label="Expiry from" value={expiryFrom || null} onChange={(iso) => setExpiryFrom(iso ?? '')} />
          </div>
          <div style={{ width: 144 }}>
            <DateField label="Expiry to" value={expiryTo || null} onChange={(iso) => setExpiryTo(iso ?? '')} />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {filtered.length} of {domains.length} domain{domains.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.06)' }}>
              {sortableHeader('name', 'Domain')}
              {sortableHeader('businessName', 'Business')}
              <TableCell sx={headerCellSx}>Registrant</TableCell>
              {sortableHeader('status', 'Status')}
              {sortableHeader('expiry', 'Expiry')}
              <TableCell sx={headerCellSx}>Last Invoice</TableCell>
              {sortableHeader('lastPaidDate', 'Last Paid')}
              {sortableHeader('actionOnExpiry', 'On expiry')}
              <TableCell sx={headerCellSx} align="center">Auto-renew</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading && paged.map((d, i) => {
              const expiryTs = d.expiry ? new Date(d.expiry).getTime() : null;
              const expired = expiryTs !== null && !isNaN(expiryTs) && expiryTs < today;
              return (
                <TableRow
                  key={d.id}
                  hover
                  onClick={() => navigate(`/domain/${d.id}`)}
                  sx={{ cursor: 'pointer', backgroundColor: i % 2 ? 'action.hover' : 'transparent' }}
                >
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{d.name}</TableCell>
                  <TableCell sx={bodyCellSx} onClick={editingOwnerId === d.id ? (e) => e.stopPropagation() : undefined}>
                    {editingOwnerId === d.id ? (
                      <CustomerOwnerSelect
                        dense
                        domainId={d.id}
                        currentOwnerGuid={d.ownerCustomerGuid}
                        onReassigned={(updated) => {
                          setDomains((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                          setEditingOwnerId(null);
                          setSnackbar({
                            open: true,
                            message: `Owner set to ${updated.businessName ?? 'customer'} for ${updated.name}`,
                            severity: 'success',
                          });
                        }}
                        onCancel={() => setEditingOwnerId(null)}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{d.businessName ?? (d.hasOwner ? '—' : <em>no owner</em>)}</span>
                        <Tooltip title="Reassign owner">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setEditingOwnerId(d.id); }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{d.registrantEmail ?? '—'}</TableCell>
                  <TableCell sx={bodyCellSx}>{d.status ? <Chip label={d.status} size="small" color={statusColor(d.status)} /> : '—'}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: expired ? 'error.main' : 'text.primary', fontWeight: expired ? 600 : 400 }}>
                    {fmtDate(d.expiry)}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {d.invoice ? (
                      <>
                        {d.invoice.number && (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            sx={{ mr: 1 }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/invoice/${d.invoice!.number}`); }}
                          >
                            {d.invoice.number}
                          </Link>
                        )}
                        {d.invoice.rsId && (
                          <Link
                            href={`${REPAIRSHOPR_BASE}/invoices/${d.invoice.rsId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                          >
                            RS
                          </Link>
                        )}
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{fmtDate(d.lastPaidDate)}</TableCell>
                  <TableCell sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                    <Select
                      size="small"
                      variant="standard"
                      disableUnderline
                      displayEmpty
                      disabled={savingId === d.id}
                      value={actionValue(d.actionOnExpiry)}
                      onChange={(e: SelectChangeEvent) => handleActionOnExpiryChange(d, e.target.value)}
                      renderValue={(v) =>
                        v ? v : <Box component="span" sx={{ color: 'text.disabled' }}>renew</Box>
                      }
                      sx={{
                        fontSize: '0.8rem',
                        // A domain set to lapse is the exception — make it read as one.
                        color: actionValue(d.actionOnExpiry) === 'lapse' ? '#C07A10' : 'text.primary',
                        fontWeight: actionValue(d.actionOnExpiry) === 'lapse' ? 600 : 400,
                      }}
                    >
                      {ACTION_OPTIONS.map((a) => (
                        <MenuItem key={a} value={a} sx={{ fontSize: '0.8rem' }}>{a}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch size="small" checked={d.autoRenew} disabled={savingId === d.id} onChange={() => handleToggleAutoRenew(d)} />
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No domains match the current filters.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
